import React, { useEffect, useState } from "react";
import { Zap, Play, Clock, AlertCircle, CheckCircle2, TrendingDown } from "lucide-react";
import { api, formatError } from "../../lib/api.js";
import { fmtDate } from "../../lib/format.js";

export default function NpsBenchmarkPanel() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [lastRun, setLastRun] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendMonths, setTrendMonths] = useState(6);

  const load = async () => {
    const [{ data: h }, { data: t }] = await Promise.all([
      api.get("/surveys/alerts/history"),
      api.get(`/surveys/alerts/trend-comparison?months=${trendMonths}`),
    ]);
    setHistory(h);
    setTrend(t);
  };
  useEffect(() => { load(); }, [trendMonths]);

  const runNow = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/surveys/alerts/check-nps-benchmark");
      setLastRun(data);
      load();
    } catch (e) { setErr(formatError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm" data-testid="nps-benchmark-panel">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#DC7633]" />
          <h2 className="font-heading font-bold text-sm text-[#1A1A2E]">Alertas NPS Benchmark</h2>
        </div>
        <button
          onClick={runNow}
          disabled={loading}
          className="text-xs bg-[#1F3864] hover:bg-[#142542] text-white rounded-sm px-3 py-1.5 flex items-center gap-1 disabled:opacity-50"
          data-testid="nps-bench-run-now"
        >
          <Play className="w-3 h-3" /> {loading ? "A verificar…" : "Executar agora"}
        </button>
      </div>

      <div className="p-4 space-y-4">
        {err && <div className="px-3 py-2 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm flex items-start gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{err}</div>}

        <div className="bg-[#FAFBFC] border border-gray-200 rounded-sm p-3 text-sm">
          <div className="flex items-center gap-2 text-[#1A1A2E]">
            <Clock className="w-4 h-4 text-[#6C757D]" />
            <b>Como funciona?</b>
          </div>
          <ul className="mt-2 ml-5 list-disc text-xs text-[#6C757D] space-y-1">
            <li>O sistema compara o <b>NPS do mês actual</b> com o <b>NPS do mês anterior</b> por departamento e global.</li>
            <li>Se a queda for <b>maior que 10 pontos</b>, envia email aos managers (admin, NAM, department_manager) e cria notificação in-app.</li>
            <li>Executa automaticamente no <b>dia 1 de cada mês às 08:00</b> (horário Africa/Maputo).</li>
            <li>Também pode ser executado manualmente aqui para preview imediato.</li>
          </ul>
        </div>

        {/* Last manual run result */}
        {lastRun && (
          <div className="border border-[#1F3864]/40 bg-[#F0F6FF] rounded-sm p-3" data-testid="nps-bench-last-run">
            <div className="font-heading text-sm font-bold text-[#1F3864] mb-2">Última execução · {lastRun.alerts_count} alerta(s)</div>
            <ReportTable report={lastRun.report} />
          </div>
        )}

        {/* Trend Comparison (iter 73) */}
        {trend && (
          <div className="border border-gray-200 rounded-sm p-3 bg-white" data-testid="nps-bench-trend">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading text-sm font-bold text-[#1A1A2E]">Comparação de tendências — últimos {trendMonths} meses</div>
              <select value={trendMonths} onChange={(e) => setTrendMonths(Number(e.target.value))} className="text-xs border border-gray-300 rounded-sm px-2 py-1" data-testid="nps-bench-trend-months">
                <option value={3}>3 meses</option>
                <option value={6}>6 meses</option>
                <option value={12}>12 meses</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-[#6C757D] uppercase tracking-widest text-[10px]">
                    <th className="text-left py-1.5 px-2">Departamento</th>
                    {trend.months.map((m) => <th key={m} className="text-right py-1.5 px-2 font-mono">{m}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {trend.series.map((row) => (
                    <tr key={row.department} className="border-b border-gray-100">
                      <td className="py-1.5 px-2 font-semibold">{row.department}</td>
                      {row.scores.map((s, i) => {
                        const prev = i > 0 ? row.scores[i - 1].nps_score : null;
                        const delta = s.nps_score != null && prev != null ? s.nps_score - prev : null;
                        const colorClass = s.nps_score == null ? "text-[#9CA3AF]" :
                          s.nps_score < 0 ? "text-[#DC3545]" :
                          s.nps_score < 30 ? "text-[#DC7633]" : "text-emerald-700";
                        return (
                          <td key={i} className={`py-1.5 px-2 text-right font-mono font-semibold ${colorClass}`} title={`Base: ${s.base} respostas`}>
                            {s.nps_score != null ? Math.round(s.nps_score) : "—"}
                            {delta != null && (
                              <span className={`ml-1 text-[10px] ${delta >= 0 ? "text-emerald-600" : "text-[#DC3545]"}`}>
                                {delta > 0 ? "▲" : delta < 0 ? "▼" : ""}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[10px] text-[#6C757D] italic">Verde ≥30 · Laranja 0-29 · Vermelho &lt;0. ▲▼ indicam variação face ao mês anterior.</div>
          </div>
        )}

        {/* History */}
        <div>
          <div className="font-heading text-sm font-bold text-[#1A1A2E] mb-2">Histórico ({history.length})</div>
          {history.length === 0 ? (
            <div className="text-sm text-[#6C757D] italic text-center py-6 bg-[#FAFBFC] border border-gray-200 rounded-sm">Sem execuções registadas.</div>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <details key={h.id} className="border border-gray-200 rounded-sm" data-testid={`nps-bench-run-${h.id}`}>
                  <summary className="px-3 py-2 cursor-pointer hover:bg-[#F4F6F8] text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {h.alerts.length > 0 ? <TrendingDown className="w-4 h-4 text-[#DC3545]" /> : <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      <span className="font-mono text-xs">{fmtDate(h.run_at)}</span>
                      <span className="text-xs text-[#6C757D]">· {h.period_prev} → {h.period_cur}</span>
                    </span>
                    <span className={`text-xs font-semibold ${h.alerts.length > 0 ? "text-[#DC3545]" : "text-emerald-700"}`}>
                      {h.alerts.length > 0 ? `${h.alerts.length} alerta(s): ${h.alerts.join(", ")}` : "Sem alertas"}
                    </span>
                  </summary>
                  <div className="px-3 pb-3">
                    <ReportTable report={h.report} />
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportTable({ report }) {
  if (!report || report.length === 0) return <div className="text-xs text-[#6C757D] italic">Sem dados.</div>;
  return (
    <table className="w-full text-xs mt-2">
      <thead>
        <tr className="border-b border-gray-200 text-[#6C757D] uppercase tracking-widest text-[10px]">
          <th className="text-left py-1.5 px-2">Departamento</th>
          <th className="text-right py-1.5 px-2">NPS anterior</th>
          <th className="text-right py-1.5 px-2">NPS actual</th>
          <th className="text-right py-1.5 px-2">Variação</th>
          <th className="text-right py-1.5 px-2">Respostas</th>
        </tr>
      </thead>
      <tbody>
        {report.map((r, idx) => (
          <tr key={idx} className={`border-b border-gray-100 ${r.alert ? "bg-[#FEF3F2]" : ""}`}>
            <td className="py-1.5 px-2 font-semibold">{r.department}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.previous?.nps_score ?? "—"}</td>
            <td className="py-1.5 px-2 text-right font-mono">{r.current?.nps_score ?? "—"}</td>
            <td className={`py-1.5 px-2 text-right font-mono font-semibold ${r.alert ? "text-[#DC3545]" : r.delta != null && r.delta > 0 ? "text-emerald-700" : "text-[#1A1A2E]"}`}>
              {r.delta != null ? `${r.delta > 0 ? "+" : ""}${r.delta}` : "—"}
            </td>
            <td className="py-1.5 px-2 text-right text-[#6C757D]">
              {(r.previous?.base ?? 0)} / {(r.current?.base ?? 0)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
